import { lazy, Suspense, useEffect, useState } from "react";

// Deep import — the dashboard barrel would pull react-grid-layout eagerly.
import { ErrorBoundary } from "dashboard/ErrorBoundary";
import DelayedSpinner from "components/common/DelayedSpinner";
import { PAGE_LOADERS } from "./pageLoaders";
import { usePage } from "contexts/PageContext";
import { DEFAULT_PAGE, isLandingPage } from "contexts/pageParams";
import { useAppUI } from "contexts/UIContext";
import Alert from "components/common/Alert";
import Sidebar from "../Sidebar";
import { isSearchChord, isTypingTarget } from "../navShortcuts";
import { focusSearchBox } from "../searchBoxes";
import { useSearchFlight } from "../useSearchFlight";

const SeasonalityPage = lazy(PAGE_LOADERS["seasonality"]);
const EuEtsReport = lazy(PAGE_LOADERS["eu-ets-report"]);
const Token = lazy(PAGE_LOADERS["token"]);
const UpdatedVentura = lazy(PAGE_LOADERS["ventura-port"]);
const FFAOverviewPage = lazy(PAGE_LOADERS["ffa-overview"]);
const SeaMarginPage = lazy(PAGE_LOADERS["sea-margin"]);
const WeatherInPortsPage = lazy(PAGE_LOADERS["weather-in-ports"]);
const TeamEstimatesPage = lazy(PAGE_LOADERS["team-estimates"]);
const VesselViewPage = lazy(PAGE_LOADERS["vessel-view"]);
const BusinessCardPage = lazy(PAGE_LOADERS["business-card"]);
const DirectoryPage = lazy(PAGE_LOADERS["directory"]);
const EmissionReportPage = lazy(PAGE_LOADERS["emission-report"]);
const FileSharingPage = lazy(PAGE_LOADERS["file-sharing"]);
const FdtrStrategiesPage = lazy(PAGE_LOADERS["fdtr-strategies"]);
const TradesPage = lazy(PAGE_LOADERS["recap-manager"]);
const DataloyPage = lazy(PAGE_LOADERS["dataloy"]);
const CargoPlanPage = lazy(PAGE_LOADERS["cargo-plan"]);
const CargoListPage = lazy(PAGE_LOADERS["cargo-orders"]);
const SignalsPage = lazy(PAGE_LOADERS["signals"]);
const NavilexV2Page = lazy(PAGE_LOADERS["navilex-v2"]);
const OptionsPage = lazy(PAGE_LOADERS["options"]);
const LandingPage = lazy(PAGE_LOADERS[DEFAULT_PAGE]);

const PageFallback = () => <DelayedSpinner delayMs={400} />;

// The pane's slide-in/out, matched by the width transition in RootPage/styles.scss.
const PANE_MS = 220;

function MainContent() {
  const { page } = usePage();
  const {
    setAppLoading,
    setAppModal,
    setAlert,
    setSidebarOpen,
    sidebarOpen,
    alertProps,
  } = useAppUI();
  const [focusSearchSignal, setFocusSearchSignal] = useState(0);
  const isLanding = isLandingPage(page);

  // The pane mounts in the same commit the menu opens (so the search flight
  // can measure its box at once) and stays mounted while it slides out.
  const [closing, setClosing] = useState(false);
  const [wasOpen, setWasOpen] = useState(sidebarOpen);
  if (wasOpen !== sidebarOpen) {
    setWasOpen(sidebarOpen);
    if (!sidebarOpen) setClosing(true);
  }
  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(() => setClosing(false), PANE_MS);
    return () => window.clearTimeout(timer);
  }, [closing]);
  const paneMounted = sidebarOpen || closing;

  // The slide runs off a class added one frame after mount.
  const [paneShown, setPaneShown] = useState(sidebarOpen);
  useEffect(() => {
    if (!sidebarOpen) {
      setPaneShown(false);
      return;
    }
    const raf = requestAnimationFrame(() => setPaneShown(true));
    return () => cancelAnimationFrame(raf);
  }, [sidebarOpen]);

  // One search box at a time: the page's box flies into the pane and back.
  useSearchFlight(sidebarOpen);

  // A click anywhere outside the pane closes it; the hamburger keeps its own toggle.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Element | null;
      if (target?.closest(".wb-menu-pane, .wb-nav__menu-btn")) return;
      setSidebarOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [sidebarOpen, setSidebarOpen]);

  // "/" anywhere opens the menu with its search box focused; Cmd/Ctrl+K
  // lands in the visible search: the pane's, the navbar's, or the landing
  // page's own box.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (isSearchChord(e)) {
        e.preventDefault();
        focusSearchBox(sidebarOpen ? ["menu", "nav", "hero"] : ["nav", "hero", "menu"]);
        return;
      }
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setSidebarOpen(true);
      setFocusSearchSignal((n) => n + 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [setSidebarOpen, sidebarOpen]);

  // A closed menu drops the signal, so a later hamburger open does not
  // re-run the focus a past "/" asked for.
  useEffect(() => {
    if (!sidebarOpen) setFocusSearchSignal(0);
  }, [sidebarOpen]);

  const searchParams = new URLSearchParams(window.location.search);
  const parsedParams: { [key: string]: string } = {};
  searchParams.forEach((value, key) => {
    if (
      ["id_token", "access_token", "session_state", "code", "state"].includes(
        key,
      )
    ) {
      return;
    }
    parsedParams[key] = value;
  });

  useEffect(() => {
    /*
      Reset global UI components on first render
      to avoid blocking the screen
    */
    setAppLoading(false);
    setAppModal(null);
    setAlert(null);
  }, [page]);

  const renderContent = () => {
    switch (page) {
      case "ventura-port":
        return <UpdatedVentura estimateId={parsedParams["estimateId"]} />;
      case "eu-ets-report":
        return <EuEtsReport />;
      case "token":
        return <Token />;
      case "seasonality":
        return <SeasonalityPage />;
      case "ffa-overview":
        return <FFAOverviewPage />;
      case "sea-margin":
        return <SeaMarginPage />;
      case "emission-report":
        return <EmissionReportPage />;
      case "file-sharing":
        return <FileSharingPage />;
      case "weather-in-ports":
        return <WeatherInPortsPage />;
      case "team-estimates":
        return <TeamEstimatesPage />;
      case "vessel-view":
        return <VesselViewPage />;
      case "business-card":
        return <BusinessCardPage />;
      case "directory":
        return <DirectoryPage />;
      case "fdtr-strategies":
        return <FdtrStrategiesPage />;
      case "recap-manager":
        return <TradesPage />;
      case "dataloy":
        return <DataloyPage />;
      case "cargo-plan":
        return <CargoPlanPage />;
      case "cargo-orders":
        return <CargoListPage />;
      case "signals":
        return <SignalsPage />;
      case "navilex-v2":
        return <NavilexV2Page />;
      case "options":
        return <OptionsPage />;
      default:
        return <LandingPage />;
    }
  };

  return (
    <div className="root-page-wrapper container-fluid p-0 m-0">
      {/* Sidebar */}
      {paneMounted ? (
        <div className={`wb-menu-pane${paneShown ? " is-shown" : ""}`}>
          <Sidebar focusSearchSignal={focusSearchSignal} />
        </div>
      ) : null}

      <main
        className={`root-page${
          page === "seasonality" ||
          page === "ffa-overview" ||
          page === "sea-margin" ||
          page === "weather-in-ports" ||
          page === "team-estimates" ||
          page === "sea-margin" ||
          page === "emission-report" ||
          page === "file-sharing" ||
          page === "vessel-view" ||
          page === "directory" ||
          page === "fdtr-strategies" ||
          page === "recap-manager" ||
          page === "dataloy" ||
          page === "cargo-plan" ||
          page === "cargo-orders" ||
          page === "options" ||
          page === "signals" ||
          page === "navilex-v2" ||
          isLanding
            ? " root-page--wide"
            : ""
        }`}
      >
        {/* Alert */}
        {alertProps ? <Alert {...alertProps} /> : null}

        {/* Page Content — keyed boundary resets on navigation; outside
            Suspense so failed lazy-chunk loads (post-deploy hash rotation)
            land on the retry card too. */}
        <ErrorBoundary key={page || "default"} label={page || "page"}>
          <Suspense fallback={<PageFallback />}>{renderContent()}</Suspense>
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default MainContent;
